import classNames from 'classnames';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import type {
    NumericStepperBaseProps,
    UncontrolledNumericStepperHandle,
} from './NumericStepper.types';

import { trimStart } from '../../../utils/stringUtils';
import { useNumericStepperCalculation } from './NumericStepper.hooks';
import styles from './NumericStepper.module.scss';
import NumericStepperAddButton from './NumericStepperAddButton';
import NumericStepperSubtractButton from './NumericStepperSubtractButton';

interface UncontrolledNumericStepperProps extends NumericStepperBaseProps {
    defaultValue?: number;
    onConfirm?: (newValue: number, prevValue: number) => void;
}

const UncontrolledNumericStepper = forwardRef<
    UncontrolledNumericStepperHandle,
    UncontrolledNumericStepperProps
>(({ onConfirm, size = 'default', ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const prevValueRef = useRef(props.defaultValue || 0);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    const { calculateAdditionValue, calculateSubstractValue, minMaxGuard } =
        useNumericStepperCalculation({
            incrementBy: props.incrementBy,
            minValue: props.minValue,
            maxValue: props.maxValue,
        });

    useImperativeHandle(ref, (): UncontrolledNumericStepperHandle => {
        return {
            setValue: (_value: number) => {
                if (inputRef.current) {
                    const value = minMaxGuard(_value);
                    prevValueRef.current = inputRef.current.valueAsNumber;
                    inputRef.current.valueAsNumber = value;
                }
            },
            revertValue: () => {
                if (inputRef.current) {
                    inputRef.current.valueAsNumber = prevValueRef.current;
                }
            },
            focusAddButton: () => {
                addButtonRef.current?.focus();
            },
            value: inputRef.current?.valueAsNumber,
        };
    }, [minMaxGuard]);

    const confirmValue = useCallback(
        (newValue: number, prevValue: number) => {
            if (inputRef.current) inputRef.current.valueAsNumber = newValue;
            if (newValue !== prevValue) {
                onConfirm?.(newValue, prevValue);
            }
        },
        [onConfirm],
    );

    const handleInputConfirm = useCallback(
        (newValue: number, prevValue: number) => {
            let finalNewValue: number;
            // when user clears the input completely by backspace it will be NaN
            if (Number.isNaN(newValue)) {
                finalNewValue = prevValue || 0; // if user clears input and tabs out, revert to previous value
            } else {
                finalNewValue = minMaxGuard(newValue);
            }
            confirmValue(finalNewValue, prevValue);
        },
        [confirmValue, minMaxGuard],
    );

    return (
        <div
            className={classNames(
                styles.Wrapper,
                props.fullWidth && styles.FullWidth,
                props.className,
            )}
        >
            <div
                className={classNames(
                    styles.Container,
                    size === 'default' && styles.SizeDefault,
                    size === 'large' && styles.SizeLarge,
                )}
            >
                <NumericStepperSubtractButton
                    aria-label={props.removeButtonLabel}
                    onClick={(e) => {
                        if (inputRef.current) {
                            const newValue = calculateSubstractValue(
                                inputRef.current.valueAsNumber,
                            );
                            prevValueRef.current = inputRef.current.valueAsNumber;
                            confirmValue(newValue, inputRef.current.valueAsNumber);
                        }
                        props.onRemoveClick?.(e);
                    }}
                />
                <input
                    ref={inputRef}
                    defaultValue={props.defaultValue || 0}
                    onKeyUp={(e) => {
                        if (e.key === 'Enter') {
                            handleInputConfirm(e.currentTarget.valueAsNumber, prevValueRef.current); // will trigger handleInputConfirm below. TODO remove the blur after UX is done with the new toast-like flow of adding to cart
                        }
                    }}
                    onBlur={(e) =>
                        handleInputConfirm(e.currentTarget.valueAsNumber, prevValueRef.current)
                    }
                    type="number"
                    pattern="\d*"
                    min={props.minValue}
                    max={props.maxValue}
                    onFocus={(e) => {
                        prevValueRef.current = e.currentTarget.valueAsNumber;
                        e.currentTarget.select();
                    }}
                    onClick={(e) => {
                        prevValueRef.current = e.currentTarget.valueAsNumber;
                        e.currentTarget.select();
                    }}
                    onChange={(e) => {
                        // disallow leading zeroes when typing a number while 0 is the current input value
                        if (
                            e.currentTarget.valueAsNumber !== 0 &&
                            e.currentTarget.value.startsWith('0')
                        ) {
                            e.currentTarget.valueAsNumber = parseInt(
                                trimStart(e.currentTarget.value, '0'),
                                10,
                            );
                        }
                    }}
                    className={styles.Input}
                    data-test="numericstepper-productitemquantityinput"
                    aria-label={props.inputLabel}
                />
                <NumericStepperAddButton
                    ref={addButtonRef}
                    aria-label={props.addButtonLabel}
                    onClick={(e) => {
                        if (inputRef.current) {
                            const newValue = calculateAdditionValue(inputRef.current.valueAsNumber);
                            prevValueRef.current = inputRef.current.valueAsNumber;
                            confirmValue(newValue, inputRef.current.valueAsNumber);
                        }
                        props.onAddClick?.(e);
                    }}
                />
            </div>
        </div>
    );
});

export default UncontrolledNumericStepper;
